import React, { useState, Fragment, useEffect, useMemo, useCallback } from 'react';
import { useAppDispatch, useAppSelector } from '../../../store/configureStore';

import Button from '../../themed/button';
import Conditional from '../../common/conditional';
import CustomCoverForm from '../../forms/custom-fvi/coverForm';
import CustomUTMSection from '../custom-utm/custom-utm-section';
import ConcreteHammerTemplate from '../custom-pcnl/concrete-hammer-template/report-form';
import Elevation from '../../themed/instruction-modals/elevation';
import ExecutiveSummaryForm from '../../forms/visual-inspection/executiveSummaryForm';
import FVICoverData from '../../themed/fvi-cover-data';
import LoadingBadge from '../../themed/loading-badge';
import Modal from '../../themed/modal';
import NDTStructuralReport from './NDTStructuralReport';
import ReportTitleForm from '../../forms/custom-fvi/reportTitleForm';
import Switch from '../../themed/switch';
import StructureItems from './structure-items';
import SyncTime from '../../blocks/sync-time';
import Zippy from '../../themed/zippy';

import { getTutorials } from '../../../store/tutorials';
import { getCoverData, initialFVICoverData } from '../../../store/coverData';
import { getCurrentUser } from '../../../services/authService';
import { getReportDetails, setItemsPerPage, setLastPhotoNumber } from '../../../store/reportDetails';

import { matchPhrases, reArrangeInspectionItemAnomalies, reArrangeGeneralPhotos, validateWorkOrderNumber, reArrangePhotoRecord } from '../../../utils/utilities';
import { DEFAULT_REPORT_TITLE, TABLE_ITEM_COUNT_LIMITS, tutorialConstants } from '../../../utils/constants';
import { AnomalyLocationData, ChecklistItem, ConcreteHammerData, CustomUTMOverview, FVIStructureItem, IncludeSummary, PDFImageProps, PIDData, User, UTMInspection } from '../../../utils/models';

import useAutoSaveReport from '../../../hooks/useAutoSave';
import useIncompleteInspectedSections from '../../../hooks/useIncompleteInspectedSections';
import useInspectedItemIDUpdate from '../../../hooks/useInspectedItemIDUpdate';
import useProjectDetails from '../../../hooks/useProjectDetails';

import config from '../../../utils/config';
import toastService from '../../../services/toastService';

const CustomNDTStructuralReport: React.FC = () => {
	const { projectDetails, error } = useProjectDetails();
	const { activeTutorial } = useAppSelector(getTutorials);
	const { fviCoverData } = useAppSelector(getCoverData);
	const { isSaving } = useAppSelector(getReportDetails);

	const [structureItems, setStructureItems] = useState<FVIStructureItem[]>([]);
	const [includeSummary, setIncludeSummary] = useState<IncludeSummary>('include');
	const [pidData, setPIDData] = useState<PIDData>({ hasPID: 'yes', pIDNumber: '', pIDImages: [] });
	const [anomalyLocationData, setAnomalyLocationData] = useState<AnomalyLocationData>({ hasAnomalyLocations: 'yes', anomalyLocations: [] });
	const [additionalComment, setAdditionalComment] = useState('');
	const [generalPhotos, setGeneralPhotos] = useState<PDFImageProps[]>([]);
	const [checklistItems, setChecklistItems] = useState<ChecklistItem[]>(initialChecklistItems);
	const [previewReport, setPreviewReport] = useState<boolean>(false);
	const [coverData, setCoverData] = useState(initialFVICoverData);
	const [executiveSummary, setExecutiveSummary] = useState('');
	const [reportTitle, setReportTitle] = useState(DEFAULT_REPORT_TITLE);
	const [tmlLocations, setTMLLocations] = useState<PDFImageProps[]>([]);
	const [utInspectionOverview, setUTInspectionOverview] = useState(initialUTMOverview);
	const [utInspectionItems, setUTInspectionItems] = useState<UTMInspection[]>([]);
	const [concreteReport, setConcreteReport] = useState<ConcreteHammerData>(initialConcreteData);

	const dispatch = useAppDispatch();

	const { role } = getCurrentUser() as User;
	const { isLastInspectedItemComplete } = useIncompleteInspectedSections(structureItems)

	const reportDetails = useMemo(() => {
			return {
				additionalComment,
				coverData,
				checklistItems,
				executiveSummary,
				includeSummary,
				pidData,
				anomalyLocationData,
				projectDetails,
				reportTitle,
				structureItems,
				generalPhotos,
				utInspectionItems,
				inspectionOverview: utInspectionOverview,
				concreteHammerData: concreteReport,
				tmlLocations,
			};
	}, [projectDetails, coverData, checklistItems, concreteReport, additionalComment, anomalyLocationData, pidData, executiveSummary, generalPhotos, structureItems, utInspectionItems, utInspectionOverview, tmlLocations, includeSummary, reportTitle]);

	const validationMessage = useMemo(() => {
		const { anomalyLocationData, concreteHammerData, coverData, pidData, tmlLocations, utInspectionItems, } = reportDetails;

		if (!validateWorkOrderNumber(coverData.workOrderNumber)) {
			return 'Enter a valid work order number!';
		}

		if (matchPhrases(pidData.hasPID, 'yes') && pidData.pIDImages.length === 0) {
			return 'Please Upload P & ID images!';
		}

		if (reportDetails.concreteHammerData.reading.items.length > 0 && reportDetails.concreteHammerData.locations.length === 0) {
			return 'Please provide valid concrete locations'
		}

		if (anomalyLocationData.anomalyLocations.length === 0) {
			if (utInspectionItems.length > 0) {
				return 'Anomaly locations must be provided for UTM Inspection!';
			}

			if (matchPhrases(anomalyLocationData.hasAnomalyLocations, 'yes')) {
				return 'Please Upload Anomaly Location images!';
			}
		}

		if (utInspectionItems.length > 0) {
			if (tmlLocations.length === 0) {
				return 'Provide TML Locations';
			}

			if (utInspectionOverview.equipment.length === 0) {
				return 'Please complete UTM Inspection Overview';
			}
		}
		
		if (concreteHammerData.reading.items.length > 0) {
			if (concreteHammerData.locations.length === 0) {
				return 'Provide Concrete Locations';
			}

			if (concreteHammerData.overview.equipment.length === 0) {
				return 'Complete the Concrete Report Overview!';
			}
		}

		if (!isLastInspectedItemComplete) {
			return 'Please fill all fields first!';
		}

		return '';
	}, [isLastInspectedItemComplete, utInspectionOverview.equipment, reportDetails]);

	const togglePreviewReport = useCallback((): void => {
		if (config.enableReportCompletionBeforePublish) {
			if (validationMessage) return toastService.error(validationMessage);
		}

		setPreviewReport((p: boolean) => !p);
	}, [validationMessage]);

	const handleSaveAndPreview = (): void => {
		autoSave.saveReport();
		togglePreviewReport();
	};

	const handleToggleSummary = useCallback(() => {
		setIncludeSummary((previous) => previous === 'include' ? 'exclude' : 'include');
	}, []);

	const autoSave = useAutoSaveReport(reportDetails);
	useInspectedItemIDUpdate(structureItems.map((item) => item.structureMemberName));

	useEffect(() => {
		function initializeReport() {
			const cachedReport = autoSave.oldReportData as typeof reportDetails | null;
			if (!cachedReport) {
				if (fviCoverData.coverData.length > 0) setCoverData(fviCoverData);

				return;
			}

			if (cachedReport.structureItems) {
				const rearrangedAnomalies = reArrangeInspectionItemAnomalies(cachedReport.structureItems) as any[];
				const rearrangedGeneralPhotos = reArrangeGeneralPhotos(rearrangedAnomalies) as any[];
				setStructureItems(rearrangedGeneralPhotos);
			}

			if (cachedReport.includeSummary) {
				setIncludeSummary(cachedReport.includeSummary);
			}

			if (cachedReport.generalPhotos) {
				const photoRecord = reArrangePhotoRecord(cachedReport.generalPhotos);
				setGeneralPhotos(photoRecord);
				dispatch(setLastPhotoNumber(photoRecord.length));
			}

			if (cachedReport.tmlLocations) {
                setTMLLocations(cachedReport.tmlLocations);
            }
			
			setConcreteReport(cachedReport.concreteHammerData);
			setUTInspectionItems(cachedReport.utInspectionItems);
			setUTInspectionOverview(cachedReport.inspectionOverview);
			setAdditionalComment(cachedReport.additionalComment);
			setChecklistItems(cachedReport.checklistItems);
			setCoverData(cachedReport.coverData.coverData.length > 0 ? cachedReport.coverData : fviCoverData);
			setAnomalyLocationData(cachedReport.anomalyLocationData);
			setPIDData(cachedReport.pidData);
			setReportTitle(cachedReport.reportTitle);
			setExecutiveSummary(cachedReport.executiveSummary);
		}

		initializeReport();
	}, [autoSave.oldReportData, dispatch, fviCoverData]);

	useEffect(() => {
		dispatch(setItemsPerPage(TABLE_ITEM_COUNT_LIMITS.EFVIS));
	}, [dispatch]);

	return (
		<Fragment>
			<Modal isVisible={previewReport} onClose={togglePreviewReport}>
				<NDTStructuralReport {...reportDetails} />
			</Modal>

			<div className="mb-4">
				<SyncTime lastSynced={autoSave.lastSaved} />
			</div>

			{projectDetails && (
				<FVICoverData
					coverDataId={config.coverDataTemplates.ndtStructuralReport}
					isVisible={projectDetails.inspectionTypeId === config.supportedTemplates.ndtStructuralReport && role.approveReports}
					subtitle={`Update the Cover Data information. This is the default data used on all ${projectDetails.organisationName} NDT Structural Reports`}
				/>
			)}

			<Zippy headerTitle="Report Title" isValid={reportTitle.length > 0}>
				<ReportTitleForm
					projectTitle={projectDetails?.projectTitle}
					onAdd={setReportTitle}
				/>
			</Zippy>

			<Zippy headerTitle="Cover Sheet" isValid={coverData.coverData.length > 0}>
				<CustomCoverForm
					initialData={coverData}
					onAddCoverForm={setCoverData}
				/>
			</Zippy>

			<Zippy headerTitle="Executive Summary" isValid={executiveSummary.length > 0}>
				<div className="d-flex justify-content-end mt-2">
					<Switch 
						checked={includeSummary === 'include'}
						label='Include Inspection Breakdown'
						onChange={handleToggleSummary} 
					/>
				</div>
				
				<div className="my-4 divider"></div>

				<ExecutiveSummaryForm
					onProceed={setExecutiveSummary}
					initialSummary={executiveSummary}
				/>
			</Zippy>

			<StructureItems
				hasPDO='no'
				onChangeHasPDO={() => {}}
				pdoChecklistItems={[]}
				onChangePDOChecklistItems={() => {}}
				additionalComment={additionalComment}
				checklistLabel="STRUCTURE"
				checklistItems={checklistItems} 
				onChangeAdditionalComment={setAdditionalComment}
				onChangeChecklistItems={setChecklistItems}
				onChangeAnomalyLocationData={setAnomalyLocationData}
				anomalyLocationData={anomalyLocationData}
				generalPhotos={generalPhotos}
				onChangeGeneralPhotos={setGeneralPhotos}
				pidData={pidData}
				onChangePIDData={setPIDData}
				items={structureItems} 
				onItemsChange={setStructureItems} 
			/>
			
			<Zippy headerTitle="UTM Report (Optional)" isValid={utInspectionItems.length > 0}>
				<CustomUTMSection
					tmlLocations={tmlLocations}
					onChangeTMLLocations={setTMLLocations}
					utmItems={utInspectionItems}
					utmOverview={utInspectionOverview}
					onItemsChange={setUTInspectionItems}
					onOverviewChange={setUTInspectionOverview}
				/>
			</Zippy>

			<Zippy headerTitle="Concrete Hammer Test Report (Optional)" isValid={concreteReport.reading.items.length > 0}>
				<ConcreteHammerTemplate
					item={concreteReport} 
					onDataChange={(report) => setConcreteReport(report)}
				/>
			</Zippy>
			
			<div className="d-flex justify-content-between align-items-center">
				<div>
					<Conditional isVisible={autoSave.isProcessing}>
						<LoadingBadge message="Saving" />
					</Conditional>
				</div>

				<Elevation isElevated={activeTutorial === tutorialConstants.PREVIEW_REPORT}>
					<Button
						label="Save and Preview Report"
						disabled={error || autoSave.isProcessing || isSaving}
						onClick={handleSaveAndPreview}
					/>
				</Elevation>
			</div>
		</Fragment>
	);
};

const initialChecklistItems: ChecklistItem[] = [
	{
		id: '1',
		lineNumber: 'Coating condition',
		assessment: '',
		anomalyItems: [],
		error: true,
		comment: '',
		category: '',
	},
	{
		id: '2',
		lineNumber: 'Corrosion',
		assessment: '',
		anomalyItems: [],
		error: true,
		comment: '',
		category: '',
	},
	{
		id: '3',
		lineNumber: 'Mechanical Damage',
		assessment: '',
		anomalyItems: [],
		error: true,
		comment: '',
		category: '',
	},
	{
		id: '4',
		lineNumber: "Vibration",
		assessment: '',
		anomalyItems: [],
		error: true,
		comment: '',
		category: '',
	},
	{
		id: '5',
		lineNumber: 'Weld Joint Inspection',
		assessment: '',
		anomalyItems: [],
		error: true,
		comment: '',
		category: '',
	},
	{
		id: '6',
		lineNumber: 'Air Gap Survey',
		assessment: '',
		anomalyItems: [],
		error: true,
		comment: '',
		category: '',
	},
	{
		id: '7',
		lineNumber: 'Cathodic Protection (CP) Survey',
		assessment: '',
		anomalyItems: [],
		error: true,
		comment: '',
		category: '',
	},
	{
		id: '8',
		lineNumber: 'Ladders',
		assessment: '',
		anomalyItems: [],
		error: true,
		comment: '',
		category: '',
	},
	{
		id: '9',
		lineNumber: 'Stairways',
		assessment: '',
		anomalyItems: [],
		error: true,
		comment: '',
		category: '',
	},
	{
		id: '10',
		lineNumber: 'Walk ways',
		assessment: '',
		anomalyItems: [],
		error: true,
		comment: '',
		category: '',
	},
	{
		id: '11',
		lineNumber: 'Handrails',
		assessment: '',
		anomalyItems: [],
		error: true,
		comment: '',
		category: '',
	},
	{
		id: '12',
		lineNumber: 'Hoses',
		assessment: '',
		anomalyItems: [],
		error: true,
		comment: '',
		category: '',
	},
	{
		id: '13',
		lineNumber: 'Beams',
		assessment: '',
		anomalyItems: [],
		error: true,
		comment: '',
		category: '',
	},
	{
		id: '14',
		lineNumber: 'Bracing',
		assessment: '',
		anomalyItems: [],
		error: true,
		comment: '',
		category: '',
	},
];

const initialUTMOverview: Omit<CustomUTMOverview, 'data'> = {
	ndeTechnique: '',
	equipment: '',
	probe: '',
	amplitudeLinearityCheck: '',
	screenHeightLinearityCheck: '',
	calibrationBlk: '',
	refStd: '',
	serialNo: '',
	scanningLevel: '',
	couplant: '',
	remarks: '',
	material: '',
	calibrationDueDate: new Date(),
	dateOfUTM: null,
	corrosionAllowance: 0,
	unit: 'mm',
	summary: '',
	techniqueType: '',
	velocity: 0,
};

const initialConcreteData: ConcreteHammerData = {
	overview: {
		testDate: new Date(),
		surfaceCondition: 'As cleaned & Rough',
		stageOfTest: 'Aged Concreate',
		index: 0,
		hammerOrientation: 'Vertical',
		equipment: 'Dutrux 122D',
		serialNumber: '64947917',
		calibrationDueDate: new Date(),
		angle: 90,
		face: 'Bottom',
		refStd: 'ASTM C42/C42M-20; C1604/C1604M',
		acceptanceStd: 'BS 1881 - Part 202',
		material: 'Carbon steel',
		summary: '',
	},
	reading: {
		readingEndRange: 0,
		readingStartRange: 0,
		items: [],
	},
	locations: [],
};

export default CustomNDTStructuralReport;



			